import { colorNumberToRGB } from './helpers';

// interface LabColor {
//     L: number;
//     a: number;
//     b: number;
// }
interface XYZColor {
  X: number;
  Y: number;
  Z: number;
}
interface RGBColor {
  R: number;
  G: number;
  B: number;
}
interface RGBAColor extends RGBColor {
  A: number;
}

/**
 * Returns diff between c1 and c2 uMath.sing the CIEDE2000 algorithm
 * @param {LabColor} c1    Should have fields L,a,b
 * @param {LabColor} c2    Should have fields L,a,b
 * @return {float}   Difference between c1 and c2
 */
export function ciede2000(col1: number, col2: number): number {
  /**
   * Implemented as in "The CIEDE2000 Color-Difference Formula:
   * Implementation Notes, Supplementary Test Data, and Mathematical Observations"
   * by Gaurav Sharma, Wencheng Wu and Edul N. Dalal.
   */

  const c1 = rgb_to_lab(colorNumberToRGB(col1));
  const c2 = rgb_to_lab(colorNumberToRGB(col2));

  // Get L,a,b values for color 1
  const L1 = c1.L;
  const a1 = c1.a;
  const b1 = c1.b;

  // Get L,a,b values for color 2
  const L2 = c2.L;
  const a2 = c2.a;
  const b2 = c2.b;

  // Weight factors
  const kL = 1;
  const kC = 1;
  const kH = 1;

  /**
   * Step 1: Calculate C1p, C2p, h1p, h2p
   */
  const C1 = Math.sqrt(Math.pow(a1, 2) + Math.pow(b1, 2)); //(2)
  const C2 = Math.sqrt(Math.pow(a2, 2) + Math.pow(b2, 2)); //(2)

  const a_C1_C2 = (C1 + C2) / 2.0; //(3)

  const G = 0.5 * (1 - Math.sqrt(Math.pow(a_C1_C2, 7.0) / (Math.pow(a_C1_C2, 7.0) + Math.pow(25.0, 7.0)))); //(4)

  const a1p = (1.0 + G) * a1; //(5)
  const a2p = (1.0 + G) * a2; //(5)

  const C1p = Math.sqrt(Math.pow(a1p, 2) + Math.pow(b1, 2)); //(6)
  const C2p = Math.sqrt(Math.pow(a2p, 2) + Math.pow(b2, 2)); //(6)

  const h1p = hp_f(b1, a1p); //(7)
  const h2p = hp_f(b2, a2p); //(7)

  /**
   * Step 2: Calculate dLp, dCp, dHp
   */
  const dLp = L2 - L1; //(8)
  const dCp = C2p - C1p; //(9)

  const dhp = dhp_f(C1, C2, h1p, h2p); //(10)
  const dHp = 2 * Math.sqrt(C1p * C2p) * Math.sin(radians(dhp) / 2.0); //(11)

  /**
   * Step 3: Calculate CIEDE2000 Color-Difference
   */
  const a_L = (L1 + L2) / 2.0; //(12)
  const a_Cp = (C1p + C2p) / 2.0; //(13)

  const a_hp = a_hp_f(C1, C2, h1p, h2p); //(14)
  const T =
    1 -
    0.17 * Math.cos(radians(a_hp - 30)) +
    0.24 * Math.cos(radians(2 * a_hp)) +
    0.32 * Math.cos(radians(3 * a_hp + 6)) -
    0.2 * Math.cos(radians(4 * a_hp - 63)); //(15)
  const d_ro = 30 * Math.exp(-Math.pow((a_hp - 275) / 25, 2)); //(16)
  const RC = Math.sqrt(Math.pow(a_Cp, 7.0) / (Math.pow(a_Cp, 7.0) + Math.pow(25.0, 7.0))); //(17)
  const SL = 1 + (0.015 * Math.pow(a_L - 50, 2)) / Math.sqrt(20 + Math.pow(a_L - 50, 2.0)); //(18)
  const SC = 1 + 0.045 * a_Cp; //(19)
  const SH = 1 + 0.015 * a_Cp * T; //(20)
  const RT = -2 * RC * Math.sin(radians(2 * d_ro)); //(21)
  const dE = Math.sqrt(
    Math.pow(dLp / (SL * kL), 2) +
      Math.pow(dCp / (SC * kC), 2) +
      Math.pow(dHp / (SH * kH), 2) +
      RT * (dCp / (SC * kC)) * (dHp / (SH * kH)),
  ); //(22)
  return dE;
}

/**
 * INTERNAL FUNCTIONS
 */
function degrees(n: number) {
  return n * (180 / Math.PI);
}
function radians(n: number) {
  return n * (Math.PI / 180);
}

function hp_f(x: number, y: number) {
  //(7)
  if (x === 0 && y === 0) return 0;
  else {
    const tmphp = degrees(Math.atan2(x, y));
    if (tmphp >= 0) return tmphp;
    else return tmphp + 360;
  }
}

function dhp_f(C1: number, C2: number, h1p: number, h2p: number) {
  //(10)
  if (C1 * C2 === 0) return 0;
  else if (Math.abs(h2p - h1p) <= 180) return h2p - h1p;
  else if (h2p - h1p > 180) return h2p - h1p - 360;
  else if (h2p - h1p < -180) return h2p - h1p + 360;
  else throw new Error();
}

function a_hp_f(C1: number, C2: number, h1p: number, h2p: number) {
  //(14)
  if (C1 * C2 === 0) return h1p + h2p;
  else if (Math.abs(h1p - h2p) <= 180) return (h1p + h2p) / 2.0;
  else if (Math.abs(h1p - h2p) > 180 && h1p + h2p < 360) return (h1p + h2p + 360) / 2.0;
  else if (Math.abs(h1p - h2p) > 180 && h1p + h2p >= 360) return (h1p + h2p - 360) / 2.0;
  else throw new Error();
}

/**
 * Returns c converted to labcolor. Uses bc as background color,
 * deaults to uMath.sing white as background color.
 * @param {rgbacolor} c should have fields R,G,B,A (case insensitive)
 * @param {rgbcolor} b should have fields R,G,B (case insensitive)
 * @return {labcolor} c converted to labcolor
 */
// function rgba_to_lab(c: RGBAColor, bc?: RGBColor) {
//     c = normalize_rgb(c);
//     const new_bc = typeof bc !== "undefined" ? normalize_rgb(bc) : { R: 255, G: 255, B: 255 };
//     const new_c = {
//         R: new_bc.R + (c.R - new_bc.R) * c.A,
//         G: new_bc.G + (c.G - new_bc.G) * c.A,
//         B: new_bc.B + (c.B - new_bc.B) * c.A,
//     };
//     return rgb_to_lab(new_c);
// }

/**
 * Returns c converted to labcolor.
 * @param {rgbcolor} c should have fields R,G,B (case insensitive)
 * @return {labcolor} c converted to labcolor
 */
function rgb_to_lab(c: RGBColor) {
  return xyz_to_lab(rgb_to_xyz(c));
}

/**
 * Returns c converted to xyzcolor.
 * @param {rgbcolor} c should have fields R,G,B (case insensitive)
 * @return {xyzcolor} c converted to xyzcolor
 */
function rgb_to_xyz(c: RGBColor) {
  c = normalize_rgb(c);
  // Based on http://www.easyrgb.com/index.php?X=MATH&H=02
  let R = c.R / 255;
  let G = c.G / 255;
  let B = c.B / 255;

  if (R > 0.04045) R = Math.pow((R + 0.055) / 1.055, 2.4);
  else R = R / 12.92;
  if (G > 0.04045) G = Math.pow((G + 0.055) / 1.055, 2.4);
  else G = G / 12.92;
  if (B > 0.04045) B = Math.pow((B + 0.055) / 1.055, 2.4);
  else B = B / 12.92;

  R *= 100;
  G *= 100;
  B *= 100;

  // Observer. = 2°, Illuminant = D65
  const X = R * 0.4124 + G * 0.3576 + B * 0.1805;
  const Y = R * 0.2126 + G * 0.7152 + B * 0.0722;
  const Z = R * 0.0193 + G * 0.1192 + B * 0.9505;
  return { X: X, Y: Y, Z: Z };
}

/**
 * Returns c converted to labcolor.
 * @param {xyzcolor} c should have fields X,Y,Z
 * @return {labcolor} c converted to labcolor
 */
function xyz_to_lab(c: XYZColor) {
  // Based on http://www.easyrgb.com/index.php?X=MATH&H=07
  const ref_Y = 100.0;
  const ref_Z = 108.883;
  const ref_X = 95.047; // Observer= 2°, Illuminant= D65
  let Y = c.Y / ref_Y;
  let Z = c.Z / ref_Z;
  let X = c.X / ref_X;
  if (X > 0.008856) X = Math.pow(X, 1 / 3);
  else X = 7.787 * X + 16 / 116;
  if (Y > 0.008856) Y = Math.pow(Y, 1 / 3);
  else Y = 7.787 * Y + 16 / 116;
  if (Z > 0.008856) Z = Math.pow(Z, 1 / 3);
  else Z = 7.787 * Z + 16 / 116;
  const L = 116 * Y - 16;
  const a = 500 * (X - Y);
  const b = 200 * (Y - Z);
  return { L: L, a: a, b: b };
}

/**
 * Returns c converted to uppercase property names (RGBA from rgba).
 * @param {(rgbcolor|rgbacolor)} c should have fields R,G,B, can have field A
 * @return {(rgbcolor|rgbacolor)} c convertered to uppercase property names
 */
function normalize_rgb(c: RGBColor | RGBAColor): RGBAColor {
  const new_c = { R: c.R || 0, G: c.G || 0, B: c.B || 0, A: 0 };
  if (typeof (c as RGBAColor).A !== 'undefined') {
    new_c.A = (c as RGBAColor).A || 0;
  }
  return new_c;
}
